import React, { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight } from 'lucide-react';
import './RateCalendar.css';

/**
 * RateCalendar Component
 *
 * Hotel × night matrix for MULTI-DATE competitor rate shops (approved design
 * 2026-07-14). Replaces the flat (checkIn × hotel) grid, which repeated every
 * hotel for every night — a 7-night, 5-hotel shop was a 35-row scroll where
 * the calendar shape disappeared.
 *
 * Reads the `rateCalendar` payload on the data_table artifact:
 *   { dates, channels: [{key,label}], medians: {date: number},
 *     hotels: [{ name, subject, nights: { [date]: {
 *       position, positionSource, channels: {key: {rate, featured, link}},
 *       lowestShown: {rate, source, link}, leak } } }] }
 *
 * Reading rules: cell tint = position rate vs that night's comp median
 * (blue below, red above); click a hotel row to expand its per-OTA sub-rows;
 * hover a cell for the full channel breakdown; ▾ = parity leak (a reseller
 * undercutting every major channel).
 */

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function dayParts(isoDate) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  const dow = DOW[d.getUTCDay()];
  return { label: isoDate.slice(5).replace('-', '/'), dow, weekend: dow === 'Fri' || dow === 'Sat' };
}

// Diverging tint vs the night's comp median: blue = priced below, red =
// above, scaled to ±45% deviation. Values stay in ink; color is relative
// position only.
function cellTint(rate, medianValue) {
  if (!Number.isFinite(rate) || !Number.isFinite(medianValue) || medianValue <= 0) return undefined;
  const dev = (rate - medianValue) / medianValue;
  const alpha = Math.min(1, Math.abs(dev) / 0.45) * 0.45;
  if (alpha < 0.04) return undefined;
  const rgb = dev < 0 ? '42, 120, 214' : '227, 73, 72';
  return { backgroundColor: `rgba(${rgb}, ${alpha.toFixed(2)})` };
}

function money(value) {
  return Number.isFinite(value) ? `$${Math.round(value).toLocaleString()}` : '—';
}

function NightTooltip({ tip }) {
  const ref = useRef(null);

  // Position after render with the REAL measured size, clamped to the
  // viewport (flip left/above the cursor near the right/bottom edges).
  // Rendered through a portal to document.body: the chat message cards
  // animate with a transform (fadeInUp), and position:fixed inside a
  // transformed ancestor anchors to that ancestor — the tooltip was landing
  // clipped at the card corner instead of at the cursor.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!tip || !el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth || 1200;
    const vh = window.innerHeight || 800;
    let left = tip.x + 14;
    let top = tip.y + 16;
    if (left + rect.width > vw - 8) left = tip.x - rect.width - 14;
    if (top + rect.height > vh - 8) top = tip.y - rect.height - 12;
    el.style.left = `${Math.max(8, left)}px`;
    el.style.top = `${Math.max(8, top)}px`;
    el.style.visibility = 'visible';
  }, [tip]);

  if (!tip) return null;
  const { hotel, date, night, medianValue } = tip;
  const delta = Number.isFinite(night?.position) && Number.isFinite(medianValue)
    ? night.position - medianValue : null;
  const { dow } = dayParts(date);
  return createPortal(
    <div className="ai-rate-cal-tip" ref={ref} style={{ left: -9999, top: -9999, visibility: 'hidden' }}>
      <div className="ai-rate-cal-tip-head">{hotel.name}</div>
      <div className="ai-rate-cal-tip-sub">{dow} {date}</div>
      {night ? (
        <table>
          <tbody>
            {(tip.channels || []).map(({ key, label }) => {
              const offer = night.channels?.[key];
              return (
                <tr key={key}>
                  <td className="dim">{label}</td>
                  <td>{offer ? money(offer.rate) : '—'}{offer?.featured ? <span className="dim"> featured</span> : null}</td>
                </tr>
              );
            })}
            <tr>
              <td className="dim">Lowest shown</td>
              <td>
                {night.lowestShown ? money(night.lowestShown.rate) : '—'}
                {night.lowestShown ? <span className="dim"> · {night.lowestShown.source}</span> : null}
              </td>
            </tr>
          </tbody>
        </table>
      ) : <div className="dim">No public rates shown</div>}
      {delta != null && (
        <div className="ai-rate-cal-tip-delta">
          {delta >= 0 ? '+' : '−'}{money(Math.abs(delta))} vs comp median ({money(medianValue)})
          {night?.leak ? <span className="leak"> · parity leak</span> : null}
        </div>
      )}
    </div>,
    document.body
  );
}

const RateCalendar = ({ calendar, title }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const [tip, setTip] = useState(null);

  // Any scroll (page or the matrix's own horizontal scroller — capture phase
  // catches both) dismisses the tooltip: the cursor is no longer over the
  // cell it describes, and mouseleave doesn't fire during scroll.
  useEffect(() => {
    if (!tip) return undefined;
    const dismiss = () => setTip(null);
    window.addEventListener('scroll', dismiss, true);
    return () => window.removeEventListener('scroll', dismiss, true);
  }, [tip]);

  const dates = useMemo(() => (Array.isArray(calendar?.dates) ? calendar.dates : []), [calendar]);
  const hotels = Array.isArray(calendar?.hotels) ? calendar.hotels : [];
  const channels = Array.isArray(calendar?.channels) ? calendar.channels : [];
  const medians = calendar?.medians || {};

  if (!dates.length || !hotels.length) return null;

  const toggle = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const showTip = (event, hotel, date) => {
    setTip({
      hotel,
      date,
      night: hotel.nights?.[date] || null,
      medianValue: medians[date],
      channels,
      x: event.clientX,
      y: event.clientY,
    });
  };

  return (
    <div className="ai-rate-cal">
      {title && <div className="ai-rate-cal-title">{title}</div>}
      <div className="ai-rate-cal-scroll">
        {/* min-width keeps the hotel column readable on long shops — past it,
            the card scrolls horizontally (name column is sticky). */}
        <table style={{ minWidth: 240 + dates.length * 150 }}>
          {/* Fixed layout so expanding a hotel's OTA sub-rows (wider content
              like "$47 · Trip.com") cannot re-trigger auto column sizing and
              shift every date column. The hotel column absorbs the slack. */}
          <colgroup>
            <col />
            {dates.map((date) => <col key={date} className="ai-rate-cal-datecol" />)}
          </colgroup>
          <thead>
            <tr>
              <th className="ai-rate-cal-th left">Hotel</th>
              {dates.map((date) => {
                const { label, dow, weekend } = dayParts(date);
                return (
                  <th key={date} className={`ai-rate-cal-th${weekend ? ' wkd' : ''}`}>
                    {label}<span className="dow">{dow}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {hotels.map((hotel) => {
              const isOpen = expanded.has(hotel.name);
              return (
                <React.Fragment key={hotel.name}>
                  <tr
                    className={`ai-rate-cal-row${isOpen ? ' open' : ''}${hotel.subject ? ' subject' : ''}`}
                    onClick={() => toggle(hotel.name)}
                  >
                    <td className="ai-rate-cal-name" title={hotel.name}>
                      <span className="nmwrap">
                        <ChevronRight size={12} className="chev" />
                        <span className="nm">{hotel.name}</span>
                        {hotel.subject && <span className="you">YOU</span>}
                      </span>
                    </td>
                    {dates.map((date) => {
                      const night = hotel.nights?.[date];
                      const rate = night?.position;
                      return (
                        <td
                          key={date}
                          className="ai-rate-cal-cell"
                          onMouseMove={(e) => showTip(e, hotel, date)}
                          onMouseLeave={() => setTip(null)}
                        >
                          {Number.isFinite(rate) ? (
                            <span className="val" style={cellTint(rate, medians[date])}>
                              {night.leak && <span className="leakflag">▾</span>}
                              {money(rate)}
                            </span>
                          ) : <span className="val na">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                  {isOpen && channels.map(({ key, label }) => (
                    <tr key={`${hotel.name}-${key}`} className="ai-rate-cal-sub">
                      <td className="subname">{label}</td>
                      {dates.map((date) => {
                        const offer = hotel.nights?.[date]?.channels?.[key];
                        return (
                          <td key={date} className="subval">
                            {offer ? (
                              offer.link
                                ? <a href={offer.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{money(offer.rate)}</a>
                                : money(offer.rate)
                            ) : '—'}
                            {offer?.featured && <span className="ad">AD</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {isOpen && (
                    <tr className="ai-rate-cal-sub last">
                      <td className="subname">Lowest shown</td>
                      {dates.map((date) => {
                        const night = hotel.nights?.[date];
                        const low = night?.lowestShown;
                        return (
                          <td key={date} className={`subval${night?.leak ? ' isleak' : ''}`}>
                            {low ? (
                              <>
                                {low.link
                                  ? <a href={low.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>{money(low.rate)}</a>
                                  : money(low.rate)}
                                <span className="src"> {low.source}</span>
                              </>
                            ) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="ai-rate-cal-legend">
        <span>
          <span className="ramp">
            <i style={{ background: 'rgba(42,120,214,.45)' }} />
            <i style={{ background: 'rgba(42,120,214,.18)' }} />
            <i style={{ background: 'var(--lodge-onyx-50, #F6F6FA)' }} />
            <i style={{ background: 'rgba(227,73,72,.18)' }} />
            <i style={{ background: 'rgba(227,73,72,.45)' }} />
          </span>
          below ← night&apos;s comp median → above
        </span>
        <span><span className="leakmark">▾</span> parity leak (reseller undercuts every major channel)</span>
        <span className="dim">— not publicly shown · click a hotel for per-channel rates</span>
      </div>
      <NightTooltip tip={tip} />
    </div>
  );
};

export default RateCalendar;
